import { DataTable } from "./DataTable";
import { DataTablePagination } from "./DataTablePagination";
import { usePagination } from "./hooks/use-pagination";
import { useSorting } from "./hooks/use-sorting";
import { DataTableProps, UsePaginationOptions } from "./types/data-table";

type DataTableWithPaginationProps<T> = {
  data: T[];
  paginationOptions?: UsePaginationOptions;
  showPagination?: boolean;
} & Omit<DataTableProps<T>, "data">;

export const DataTableWithPagination = <T,>({
  data,
  paginationOptions,
  showPagination = true,
  ...tableProps
}: DataTableWithPaginationProps<T>) => {
  const pagination = usePagination(paginationOptions);
  const sorting = useSorting();

  // First sort the data, then paginate
  const sortedData = sorting.getSortedData(data, tableProps.columns);
  const totalItems = sortedData.length;
  const totalPages = Math.ceil(totalItems / pagination.pageSize);
  const paginatedData = pagination.getPaginatedData(sortedData);

  return (
    <div className="space-y-4">
      <DataTable
        {...tableProps}
        data={paginatedData}
        sortState={sorting.sortState}
        onSort={sorting.handleSort}
      />

      {showPagination && totalItems > 0 && (
        <DataTablePagination
          currentPage={pagination.currentPage}
          totalPages={totalPages}
          showSelectedCount
          selectedItems={tableProps.selectedItems?.length || 0}
          pageSize={pagination.pageSize}
          onPageChange={pagination.setCurrentPage}
          onPageSizeChange={pagination.setPageSize}
          pageSizeOptions={paginationOptions?.pageSizeOptions}
        />
      )}
    </div>
  );
};
